import type { paths } from "../types/api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIEditorialCategory =
  paths["/api/v1/editorial-categories"]["get"]["responses"]["200"]["content"]["application/json"]["editorialCategories"][number];

export type EditorialCategory = APIEditorialCategory;

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type EditorialCategoriesListResponse =
  paths["/api/v1/editorial-categories"]["get"]["responses"]["200"]["content"]["application/json"];

export type SubmissionBpasResponse =
  paths["/api/v1/editorial-categories/submission-bpas/{publicationId}"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Constants
// ============================================================================

// Backend caps `limit` at 100 (Zod max), so each page must request at most 100 rows.
const PRACTICE_AREAS_PAGE_SIZE = 100;
// Hard safety cap to avoid an accidental infinite loop if `hasMore` is ever inconsistent.
const PRACTICE_AREAS_MAX_PAGES = 100;

// ============================================================================
// Service
// ============================================================================

export const editorialCategories = {
  getAll: async (filters?: {
    id?: number;
    publicationId?: number;
    type?: string;
    name?: string;
    countryId?: number;
    practiceGroupId?: number;
    practiceAreaId?: number;
    limit?: number;
    offset?: number;
  }): Promise<EditorialCategoriesListResponse> => {
    const params = new URLSearchParams();
    if (filters?.id !== undefined) params.append("id", filters.id.toString());
    if (filters?.publicationId !== undefined)
      params.append("publicationId", filters.publicationId.toString());
    if (filters?.type) params.append("type", filters.type);
    if (filters?.name) params.append("name", filters.name);
    if (filters?.countryId !== undefined) params.append("countryId", filters.countryId.toString());
    if (filters?.practiceGroupId !== undefined)
      params.append("practiceGroupId", filters.practiceGroupId.toString());
    if (filters?.practiceAreaId !== undefined)
      params.append("practiceAreaId", filters.practiceAreaId.toString());
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<EditorialCategoriesListResponse>(`/v1/taxonomy/editorial-categories${query}`);
  },

  /**
   * Fetches ALL practice-area editorial categories for a publication/region via
   * client-side pagination. The backend caps `limit` at 100 and defaults to 50 when
   * omitted, so paging is the only correct way to retrieve the full list.
   */
  getAllPracticeAreas: async (filters: {
    publicationId?: number;
    countryId?: number;
  }): Promise<EditorialCategory[]> => {
    const all: EditorialCategory[] = [];
    let offset = 0;

    for (let page = 0; page < PRACTICE_AREAS_MAX_PAGES; page++) {
      const resp = await editorialCategories.getAll({
        ...filters,
        type: "practice_area",
        limit: PRACTICE_AREAS_PAGE_SIZE,
        offset,
      });

      const batch = resp.editorialCategories ?? [];
      all.push(...batch);

      // Primary stop condition: backend `hasMore` flag. Defensive fallbacks: an empty
      // page or a partial page (shorter than the requested page size) means we are done.
      const hasMore = resp.pagination?.hasMore ?? false;
      if (!hasMore || batch.length === 0 || batch.length < PRACTICE_AREAS_PAGE_SIZE) {
        break;
      }

      offset += PRACTICE_AREAS_PAGE_SIZE;
    }

    return all;
  },

  getById: async (id: number): Promise<EditorialCategory | null> => {
    try {
      // Use the getAll endpoint with id filter since there's no dedicated getById endpoint
      const response = await editorialCategories.getAll({ id, limit: 1 });
      return response.editorialCategories[0] ?? null;
    } catch {
      return null;
    }
  },

  getSubmissionBpas: async (
    publicationId: number,
    countryId?: number,
  ): Promise<SubmissionBpasResponse> => {
    const params = new URLSearchParams();
    if (countryId !== undefined) params.append("countryId", countryId.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<SubmissionBpasResponse>(
      `/v1/taxonomy/editorial-categories/submission-bpas/${publicationId}${query}`,
    );
  },

  getDetailsByIds: async (
    ids: number[],
  ): Promise<{
    details: Array<{
      editorialCategoryId: number;
      regionId: number | null;
      regionName: string | null;
      practiceAreaId: number | null;
      practiceAreaName: string | null;
      practiceGroupId: number | null;
      practiceGroupName: string | null;
      tableId: number | null;
      tableName: string | null;
    }>;
  }> => {
    if (ids.length === 0) return { details: [] };
    return api.post("/v1/taxonomy/editorial-categories/details", {
      editorialCategoryIds: ids,
    });
  },
};
